import { useMediaApi } from '../hooks/useMediaApi';
import { auth } from '../config/firebase';
import { logger } from '../utils/logger';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';

const devLog = (...args: any[]) => { if (import.meta.env.DEV) logger.debug('imageUtils', args.join(' ')); };

/**
 * Checks if a URL is from Ideogram.ai
 * @param url The URL to check
 * @returns Boolean indicating if the URL is from Ideogram
 */
export const isIdeogramUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('ideogram.ai') || url.includes('cdn.ideogram.ai');
};

/**
 * Checks if a URL is from Socialness media servers
 * @param url The URL to check
 * @returns Boolean indicating if the URL is from Socialness media
 */
export const isSocialnessMediaUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('media.socialness.ai') || 
         url.includes('storage.socialness.ai') || 
         url.includes('cdn.socialness.ai');
};

/**
 * Checks if a URL is an external URL (not from Socialness media)
 * @param url The URL to check
 * @returns Boolean indicating if the URL is external
 */
export const isExternalMediaUrl = (url: string): boolean => {
  if (!url) return false;
  return !isSocialnessMediaUrl(url);
};

/**
 * Uploads an external image from a URL to our storage service
 * Uses the /storage/external-url endpoint to handle CORS issues
 * 
 * @param url The external image URL
 * @param mediaApi The mediaApi instance from useMediaApi hook
 * @param brandId Optional brand ID to associate with the uploaded image
 * @returns Promise with the URL of the uploaded image in our storage
 */
export const uploadExternalImageUrl = async (
  url: string,
  mediaApi: ReturnType<typeof useMediaApi>,
  brandId: string = ''
): Promise<string> => {
  try {
    if (!url) throw new Error('No URL provided');
    
    // Skip if it's already a Socialness media URL
    if (isSocialnessMediaUrl(url)) {
      devLog('URL is already from Socialness media, skipping upload:', url);
      return url;
    }
    
    devLog('Processing external image URL:', url);
    
    // Get Firebase auth token
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    // Use the external URL upload endpoint
    const externalUrlEndpoint = `${API_URL}/storage/external-url`;
    devLog(`Making request to ${externalUrlEndpoint}`);
    
    const response = await fetch(externalUrlEndpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        url: url,
        brandId: brandId || undefined
      })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to process external URL: ${response.status} ${response.statusText}`);
    }
    
    const responseData = await response.json();
    devLog('API response:', responseData);
    
    // Get the URL from the actual response structure
    // The API returns: {publicUrl: '...', fileName: '...', originalUrl: '...', storageInfo: {...}}
    const processedUrl = responseData.publicUrl || 
                        responseData.url || 
                        responseData.data?.publicUrl ||
                        responseData.data?.url;
    
    if (!processedUrl) {
      devLog('No URL found in API response:', responseData);
      throw new Error('No URL in response from external URL processing endpoint');
    }
    
    devLog('External URL successfully processed to:', processedUrl);
    return processedUrl;
  } catch (error) {
    devLog('Error processing external image URL:', error);
    throw error; // Rethrow to be handled by the caller
  }
}; 